import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { PlatformSocialLoginButtons } from "../components/PlatformSocialLoginButtons";
import { usePlatformAuth } from "../context/platform-auth";

export function PlatformSignInPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const redirect = searchParams.get("redirect") || "/workspaces";
	const { t } = useTranslation();
	const { checkSession } = usePlatformAuth();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setError("");
		setLoading(true);
		try {
			const res = await fetch("/api/platform/auth/sign-in/email", {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({ email, password }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setError(
					(body as { message?: string } | null)?.message ??
						t("auth.signIn.failedWithStatus", { status: res.status }),
				);
				return;
			}
			await checkSession();
			navigate(redirect);
		} catch {
			setError(t("auth.signIn.networkError"));
		} finally {
			setLoading(false);
		}
	}

	const signUpLink =
		redirect !== "/workspaces"
			? `/sign-up?redirect=${encodeURIComponent(redirect)}`
			: "/sign-up";

	return (
		<>
			<h1 className="text-2xl font-bold text-gray-900 mb-4">
				{t("auth.signIn.heading")}
			</h1>
			{error && <p className="text-red-600 text-sm mb-3">{error}</p>}
			<form onSubmit={handleSubmit} className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						{t("auth.signIn.email")}
						<input
							name="email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
						/>
					</label>
				</div>
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						{t("auth.signIn.password")}
						<input
							name="password"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							required
							className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
						/>
					</label>
				</div>
				<button
					type="submit"
					disabled={loading}
					className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
				>
					{loading ? t("auth.signIn.submitting") : t("auth.signIn.submit")}
				</button>
			</form>
			<PlatformSocialLoginButtons onError={setError} redirect={redirect} />
			<p className="mt-4 text-sm text-gray-600">
				{t("auth.signIn.noAccount")}{" "}
				<Link to={signUpLink} className="text-blue-600 hover:underline">
					{t("auth.signIn.signUpLink")}
				</Link>
			</p>
		</>
	);
}
